import type { ReactNode } from 'react';
import { Asterisco } from '../componentes/Asterisco';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import asterisco from '../estilo/comun/assets/asterisco-logo.svg';
import logo from '../estilo/comun/assets/wordmark-for-red-cropped.png';
import { TOTAL_PASOS_ALTA, textoPaso } from '../logica/altaCuenta';
import { useEsMovil } from '../marco/tamano';
import './Invitacion.css';

/**
 * Hoja de acceso roja (componentes.md §16) para las pantallas sueltas del acceso que no son de Acceso.tsx: aceptar una
 * invitación (1.10) y la tablet (1.13). Mismo fondo vivo con el asterisco grande, logo, «‹ Volver» opcional y la hoja
 * blanca; `ancha` para la tablet (dos columnas).
 */
export interface Principal {
    texto: string;
    icono: NombreIcono;
    ocupado: string;
    accion: () => void;
    desactivado?: boolean;
}

export interface Enlace {
    antes?: string;
    texto: string;
    accion?: () => void;
}

interface Props {
    giro?: number;
    /** Línea bajo el logo («Tablet sin emparejar»). */
    marca?: { icono: NombreIcono; texto: string } | null;
    volver?: { texto: string; accion: () => void } | null;
    baldosa?: [NombreIcono, string, string];
    /** «Paso N de 4» sobre el título, en el alta de un propietario nuevo (1.2, 1.6 y 1.5; tarea 490). */
    paso?: number;
    /** Pasos en total (4 en el alta del propietario; 3 en la solicitud de partner, 1.23). */
    totalPasos?: number;
    /** Texto en lugar de «Paso N de M» (p. ej. «Listo» al terminar la solicitud de partner). */
    etiquetaPaso?: string;
    titulo?: string;
    sub?: ReactNode;
    error?: string | null;
    principal?: Principal;
    ocupado?: boolean;
    enlaces?: readonly Enlace[];
    ancha?: boolean;
    children?: ReactNode;
    pie?: ReactNode;
}

/** «Paso N de 4» con su barra, como el dibujo del alta (`stepLabel`, `stepW`). */
export function PasoDelAlta({ paso, total = TOTAL_PASOS_ALTA, etiqueta }: { paso: number; total?: number; etiqueta?: string }) {
    return (
        <div className="rv-acceso__paso">
            <span>{etiqueta ?? (total === TOTAL_PASOS_ALTA ? textoPaso(paso) : `Paso ${String(paso)} de ${String(total)}`)}</span>
            <span aria-hidden="true">
                <span style={{ width: `${String((Math.min(paso, total) / total) * 100)}%` }} />
            </span>
        </div>
    );
}

export function HojaRoja({ giro = 40, marca = null, volver = null, baldosa, paso, totalPasos, etiquetaPaso, titulo, sub, error = null, principal, ocupado = false, enlaces, ancha = false, children, pie }: Props) {
    const movil = useEsMovil();
    return (
        <div className="rv-acceso">
            <div className="rv-vivo" aria-hidden="true" />
            <div aria-hidden="true" className="rv-acceso__hueco" style={{ marginTop: -28 }} />
            <span
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: movil ? '150vw' : 'min(120vh, 1200px)',
                    height: movil ? '150vw' : 'min(120vh, 1200px)',
                    background: 'var(--rv-red-dark)',
                    opacity: 0.6,
                    WebkitMask: `url("${asterisco}") center/contain no-repeat`,
                    mask: `url("${asterisco}") center/contain no-repeat`,
                    transform: `translate(-50%, -50%) translate(22%, 20%) rotate(${String(giro)}deg)`,
                    transition: 'transform 1000ms cubic-bezier(0.16,1,0.3,1)',
                    pointerEvents: 'none',
                }}
            />
            <div className="rv-hoja-roja__arriba">
                <img className="rv-acceso__logo" src={logo} alt="Reservaria" />
                {marca !== null && (
                    <span className="rv-hoja-roja__marca">
                        <Icono nombre={marca.icono} />
                        {marca.texto}
                    </span>
                )}
            </div>
            <div className={`rv-acceso__caja${ancha ? ' rv-hoja-roja__caja--ancha' : ''}`}>
                {volver !== null && (
                    <div className="rv-acceso__pestanas">
                        <button type="button" className="rv-acceso__volver" onClick={volver.accion}>
                            <Icono nombre="chevron-left" />
                            <span>{volver.texto}</span>
                        </button>
                    </div>
                )}
                <div className="rv-acceso__hoja">
                    {baldosa !== undefined && (
                        <span className="rv-acceso__baldosa" style={{ background: baldosa[1], color: baldosa[2] }}>
                            <Icono nombre={baldosa[0]} />
                        </span>
                    )}
                    {paso !== undefined && <PasoDelAlta paso={paso} total={totalPasos} etiqueta={etiquetaPaso} />}
                    {titulo !== undefined && <h1 className="rv-acceso__titulo">{titulo}</h1>}
                    {sub !== undefined && <div className="rv-acceso__sub">{sub}</div>}
                    {error !== null && (
                        <div role="alert" className="rv-acceso__error">
                            <Icono nombre="circle-alert" />
                            <span>{error}</span>
                        </div>
                    )}
                    {children}
                    {principal !== undefined && (
                        <button
                            type="button"
                            className="rv-acceso__principal"
                            aria-busy={ocupado}
                            disabled={principal.desactivado === true}
                            onClick={() => {
                                if (!ocupado) {
                                    principal.accion();
                                }
                            }}
                        >
                            {ocupado ? (
                                <>
                                    <Asterisco />
                                    <span>{principal.ocupado}</span>
                                </>
                            ) : (
                                <>
                                    <span>{principal.texto}</span>
                                    <Icono nombre={principal.icono} />
                                </>
                            )}
                        </button>
                    )}
                    {enlaces !== undefined && enlaces.length > 0 && (
                        <div className="rv-acceso__enlaces">
                            {enlaces.map((l) => (
                                <div key={l.texto}>
                                    {l.antes !== undefined && <span>{l.antes}</span>}
                                    <button type="button" className="rv-acceso__enlace" disabled={l.accion === undefined} onClick={l.accion}>
                                        {l.texto}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {pie}
                </div>
            </div>
            <div aria-hidden="true" className="rv-acceso__hueco" style={{ marginBottom: -28 }} />
        </div>
    );
}
